/**
 * The stock half of the API surface. Every call goes through `@/lib/api`, which owns
 * cookies, idempotency keys, problem parsing and the step-up retry, so nothing here has to
 * think about a 403 that wants a passkey.
 */
import { api } from '@/lib/api';
import type {
  LegendResponse,
  Page,
  StockPageRow,
  StockSetPriceInlineRequest,
  StockSetQtyRequest,
  StockSetQtyResponse,
  StockSetVisibleRequest,
  StockSetVisibleResponse,
} from '@elutide/shared';
import type {
  AdjustResult,
  AnyStockPageResponse,
  AnyStockRow,
  AnyStockRowDetail,
  AnyStockWacBreakdown,
  CompareResponse,
  HistoryResponse,
  LedgerPage,
  BatchRow,
  PoDetail,
  PoListResponse,
  SnoozeAck,
  StockDetail,
  StockFilters,
  StockSummary,
  SuggestionRow,
  SupplierPriceRow,
  SupplierRow,
} from './types';

function listQuery(filters: StockFilters) {
  return {
    q: filters.q.trim() === '' ? undefined : filters.q.trim(),
    status: filters.status.length > 0 ? filters.status.join(',') : undefined,
    family: filters.family === '' ? undefined : filters.family,
    visible: filters.visible,
    include_bundles: filters.include_bundles ? true : undefined,
    limit: 200,
  };
}

export const stockApi = {
  /**
   * The whole Stock overview in one request. Tiles, families, hidden SKUs, the window and
   * the thresholds all come from here, so the number on a tile and the number in a row are
   * one derivation rather than two that drift.
   */
  page: () => api.get<AnyStockPageResponse>('/stock/page'),

  rowDetail: (productId: number) => api.get<AnyStockRowDetail>(`/stock/${productId}/detail`),

  /**
   * The batches behind one Cost (WAC) cell, plus the ones left out and why. Fetched only when
   * the operator asks: the overview never calls it.
   */
  wacBreakdown: (productId: number) =>
    api.get<AnyStockWacBreakdown>(`/stock/${productId}/wac-breakdown`),

  /**
   * Compare and set. `expected_qty` is the number the operator was looking at when the edit
   * began, so a count someone changed in Woo meanwhile comes back 409 instead of being
   * silently overwritten.
   */
  setQty: (productId: number, body: StockSetQtyRequest) =>
    api.put<StockSetQtyResponse>(`/stock/${productId}/qty`, body),

  /**
   * Inline price edit. `expected_version` is the Woo `date_modified_gmt` carried on the row,
   * so a price changed in wp-admin since the page loaded is a 409, not an overwrite.
   */
  setPriceInline: (productId: number, body: StockSetPriceInlineRequest) =>
    api.put<StockPageRow>(`/stock/${productId}/price`, body),

  setVisible: (productId: number, body: StockSetVisibleRequest) =>
    api.put<StockSetVisibleResponse>(`/stock/${productId}/visible`, body),

  /** Every definition on the screen, with its version, unit, basis and sources. */
  legend: () => api.get<LegendResponse>('/legend'),

  list: (filters: StockFilters, cursor: string | null = null) =>
    api.get<Page<AnyStockRow>>('/stock', {
      query: { ...listQuery(filters), cursor: cursor ?? undefined },
    }),

  summary: () => api.get<StockSummary>('/stock/summary'),

  detail: (productId: number) => api.get<StockDetail>(`/stock/${productId}`),

  setReorder: (
    productId: number,
    input: {
      safety_days: number;
      target_cover_days: number;
      pack_size: number;
      min_order_qty: number;
      preferred_supplier_id: string | null;
    },
  ) => api.put<AnyStockRow>(`/stock/${productId}/reorder`, input),

  adjust: (
    productId: number,
    input: {
      product_id: number;
      expected_qty: number;
      delta: number;
      reason: string;
      note: string | null;
      photo_upload_id: string | null;
    },
  ) => api.post<AdjustResult>(`/stock/${productId}/adjust`, input),

  count: (
    productId: number,
    input: {
      counted_qty: number;
      photo_upload_id: string | null;
      reason: string | null;
      note: string | null;
    },
  ) => api.post<AdjustResult>(`/stock/${productId}/count`, input),

  approveAdjustment: (adjustmentId: string, decision: 'approve' | 'reject', note: string | null) =>
    api.post<{ applied: boolean }>(`/stock/adjustments/${adjustmentId}/approve`, {
      decision,
      note,
    }),

  ledger: (productId: number) => api.get<LedgerPage>(`/stock/${productId}/ledger`),

  batches: (productId: number) => api.get<Page<BatchRow>>(`/stock/${productId}/batches`),

  history: (productId: number, window: '30d' | '90d' | '365d' = '90d') =>
    api.get<HistoryResponse>(`/stock/${productId}/history`, { query: { window } }),

  exportCsv: (kind: 'stock' | 'valuation', filters: Record<string, string>, reason: string) =>
    api.post<{ export_id: string; url: string }>('/stock/export', { kind, filters, reason }),

  /* ---- purchasing ------------------------------------------------- */

  pos: (status: string | null, supplierId: string | null) =>
    api.get<PoListResponse>('/po', {
      query: { status: status ?? undefined, supplier_id: supplierId ?? undefined, limit: 100 },
    }),

  po: (poId: string) => api.get<PoDetail>(`/po/${poId}`),

  createPo: (body: {
    supplier_id: string;
    currency: string;
    expected_at: string | null;
    freight_allocation: 'units' | 'value';
    lines: {
      product_id: number;
      sku: string | null;
      qty_ordered: number;
      unit_cost_cents: number;
    }[];
    costs: { category: string; amount_cents: number; basis: string; po_line_id: string | null }[];
    notes: string | null;
  }) => api.post<PoDetail>('/po', body),

  advancePo: (poId: string, toStatus: string, reason: string | null) =>
    api.post<PoDetail>(`/po/${poId}/advance`, { to_status: toStatus, at: null, reason }),

  receivePo: (
    poId: string,
    lines: {
      po_line_id: string;
      qty_received: number;
      qty_rejected: number;
      lot_number: string | null;
    }[],
  ) => api.post<PoDetail>(`/po/${poId}/receive`, { lines }),

  payPo: (
    poId: string,
    body: { amount_cents: number; paid_on: string; method: string; reference: string | null },
  ) => api.post<PoDetail>(`/po/${poId}/payments`, body),

  suggestions: () => api.get<Page<SuggestionRow>>('/po/suggestions'),

  /**
   * Snoozing removes the SKU from the suggestion set for the length of the snooze, so the
   * server cannot find a row to echo back and answers with the acknowledgement instead. Typing
   * this as a full `SuggestionRow` was a lie: every successful call takes the short shape.
   */
  snoozeSuggestion: (productId: number, days: 7 | 14, note: string | null) =>
    api.post<SuggestionRow | SnoozeAck>(`/po/suggestions/${productId}/snooze`, { days, note }),

  /* ---- suppliers and pricing --------------------------------------- */

  suppliers: (includeInactive = false) =>
    api.get<Page<SupplierRow>>('/suppliers', {
      query: { include_inactive: includeInactive, limit: 200 },
    }),

  createSupplier: (body: Record<string, unknown>) => api.post<SupplierRow>('/suppliers', body),

  updateSupplier: (supplierId: string, body: Record<string, unknown>) =>
    api.patch<SupplierRow>(`/suppliers/${supplierId}`, body),

  supplierPrices: (productId: number | null, supplierId: string | null) =>
    api.get<Page<SupplierPriceRow>>('/supplier-prices', {
      query: {
        product_id: productId ?? undefined,
        supplier_id: supplierId ?? undefined,
        limit: 200,
      },
    }),

  createSupplierPrice: (body: Record<string, unknown>) =>
    api.post<SupplierPriceRow>('/supplier-prices', body),

  compare: (productId: number) =>
    api.get<CompareResponse>('/supplier-prices/compare', { query: { product_id: productId } }),
};
